// ABOUTME: The rain on 3A: three layers of streaks falling at their own speeds, wiped in from the top once the lower tiers have faded.
// ABOUTME: Pure CSS and decorative; a slanted band across the frame's full width, from the crown's base down to the bottom of its positioned parent.

import { Box, alpha } from '@mui/material';
import { keyframes, type Keyframes } from '@emotion/react';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import { CROSSING_RAIN_STREAK } from '../../../../theme/crossingTokens';
import { DISSOLVE_SEC } from './stormDissolve';

const RAIN_START_SEC = 2.2;
const FADE_IN_SEC = 0.6;
const WIPE_SEC = 1.5;
// The entrance is a container with 24px of padding a side, so the frame's full width is the container's plus 48px.
const BAND_WIDTH = 'calc(100cqw + 48px)';
// The reference draws the rain in design pixels that its whole stage scales; the entrance's --impact-px is one of them,
// so the drops, tiles and fall distances scale with the stack and the density matches at every size.
const PX = 'var(--impact-px)';
const SLANT = 'skewX(-16deg)';
const px = (amount: number) => `calc(${PX} * ${amount})`;

// Inline, so the placement can be read back without resolving emotion classes.
const BAND = { top: 0, width: BAND_WIDTH, transform: 'translateX(-50%)' } as const;

const fadeIn = keyframes`
  to { opacity: 1; }
`;
// The container's mask is 240% tall: sliding it up from the bottom uncovers the rain from the top down.
const wipeDown = keyframes`
  from { -webkit-mask-position: 0 100%; mask-position: 0 100%; }
  to { -webkit-mask-position: 0 0; mask-position: 0 0; }
`;
const fallNear = keyframes`
  from { background-position: 0 0; }
  to { background-position: 0 ${px(40)}; }
`;
const fallMid = keyframes`
  from { background-position: ${px(9)} ${px(13)}; }
  to { background-position: ${px(9)} ${px(62)}; }
`;
const fallFar = keyframes`
  from { background-position: ${px(18)} ${px(27)}; }
  to { background-position: ${px(18)} ${px(88)}; }
`;

const streak = (width: number, height: number, opacity: number) =>
  `radial-gradient(ellipse ${px(width)} ${px(height)} at 50% 50%, ${alpha(CROSSING_RAIN_STREAK, opacity)} 96%, transparent 100%)`;

interface RainLayer { tile: [number, number]; frames: Keyframes; sec: number; image: string }
const LAYERS: RainLayer[] = [
  { tile: [17, 40], frames: fallNear, sec: 1.3, image: streak(1.3, 7, 0.75) },
  { tile: [24, 49], frames: fallMid, sec: 1.8, image: streak(1.2, 6, 0.55) },
  { tile: [31, 61], frames: fallFar, sec: 2.2, image: streak(1.1, 8, 0.6) },
];

interface ImpactRainProps {
  still?: boolean; // there from the first paint, with no fade or wipe: the scene drawn finished (4A); the streaks still fall
  dissolving?: boolean; // fades out over DISSOLVE_SEC (4B, once forgiven); the stack removes it afterwards
}

export default function ImpactRain({ still = false, dissolving = false }: ImpactRainProps) {
  const reducedMotion = useCrossingReducedMotion();
  const noEntrance = reducedMotion || still;
  // The keyframes object is interpolated, not .name, so emotion injects its CSS when the class is built.
  const motion = (frames: Keyframes) => (reducedMotion ? 'none' : `${frames}`);

  return (
    <Box
      aria-hidden
      data-testid="impact-rain"
      data-motion={reducedMotion ? 'still' : 'moving'}
      style={dissolving
        ? { ...BAND, opacity: 0, pointerEvents: 'none', transition: `opacity ${DISSOLVE_SEC}s ease` }
        : noEntrance
        ? { ...BAND, opacity: 1, pointerEvents: 'none' }
        : {
          ...BAND, opacity: 0, pointerEvents: 'none',
          animationDuration: `${FADE_IN_SEC}s, ${WIPE_SEC}s`,
          animationDelay: `${RAIN_START_SEC}s, ${RAIN_START_SEC}s`,
        }}
      sx={{
        position: 'absolute', bottom: 0, left: '50%', zIndex: 3, overflow: 'hidden',
        animationName: noEntrance ? 'none' : `${fadeIn}, ${wipeDown}`,
        animationTimingFunction: 'ease, ease-out',
        animationFillMode: 'forwards',
        ...(noEntrance ? {} : {
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 42%, transparent 58%)',
          maskImage: 'linear-gradient(to bottom, black 0%, black 42%, transparent 58%)',
          WebkitMaskSize: '100% 240%',
          maskSize: '100% 240%',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
        }),
      }}
    >
      {LAYERS.map(layer => (
        <Box
          key={layer.tile.join('x')}
          data-testid="impact-rain-layer"
          style={{
            backgroundImage: layer.image,
            backgroundSize: `${px(layer.tile[0])} ${px(layer.tile[1])}`,
            transform: SLANT, transformOrigin: '50% 0',
            ...(reducedMotion ? {} : { animationDuration: `${layer.sec}s`, animationIterationCount: 'infinite' }),
          }}
          sx={{
            position: 'absolute', inset: '0 -50%',
            animationName: motion(layer.frames), animationTimingFunction: 'linear',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 72%, transparent 100%)',
            maskImage: 'linear-gradient(to bottom, black 0%, black 72%, transparent 100%)',
          }}
        />
      ))}
    </Box>
  );
}
